/**
 * Clip Monitor Store - State management for live clip data monitoring
 */

import { create } from 'zustand';
import {
  listClipFiles,
  getClipData,
  deleteClipData,
  getServerConfig,
  updateServerConfig,
  checkServerHealth,
  getFirmwareConstants,
  getAnnotations,
  getClipMeta,
  addAnnotation as apiAddAnnotation,
  updateAnnotation as apiUpdateAnnotation,
  deleteAnnotation as apiDeleteAnnotation,
  listBuckets as apiListBuckets,
  selectBucket as apiSelectBucket,
  listSources as apiListSources,
  selectSource as apiSelectSource,
  ClipFile,
  ClipDataPoint,
  ServerConfig,
  Annotation,
  FirmwareConstants,
  BackendMeta,
  SourceFolder,
} from '../lib/api-clip-monitor';
import { loadCriterion as apiLoadCriterion, loadAllCriteria as apiLoadAllCriteria } from '../lib/api';
import type { Criterion, Phase } from '../lib/types';

interface ClipDataState {
  // Server status
  serverOnline: boolean;
  serverConfig: ServerConfig | null;
  checkingServer: boolean;
  firmwareConstants: FirmwareConstants | null;

  // Source folders
  availableSources: SourceFolder[];
  activeSourceId: string | null;
  loadingSources: boolean;

  // Buckets
  availableBuckets: string[];
  activeBucket: string | null;
  loadingBuckets: boolean;

  // Errors
  error: string | null;
  clearError: () => void;

  // Available clips
  availableClips: ClipFile[];
  loadingClips: boolean;
  
  // Selected clip data
  selectedClipAddress: string | null;
  clipData: ClipDataPoint[];
  loadingData: boolean;
  /** Limit used when loading (null = full data). Enables "Load full data" when truncated. */
  clipDataLoadLimit: number | null;
  
  // Criterion selection (determines all thresholds)
  availableCriteria: Array<{id: number, name: string}>;
  loadingCriteria: boolean;
  currentCriterion: Criterion | null;
  currentPhase: Phase | null;
  loadingCriterion: boolean;
  changeNeededPercent: number | null; // From criterion/phase
  leakAdcThresholds: number[] | null; // From criterion diaper model
  
  // Timeline zoom/filter (brush-based selection)
  brushSelection: {
    startTime: Date | null;
    endTime: Date | null;
  };
  
  // Diaper selection (filter by diaper counter)
  selectedDiaperCounter: number | null; // null = show all, number = show specific diaper
  
  // Dynamic range toggle (Y-axis scaling)
  useDynamicRange: boolean; // When true, Y-axes scale to relevant ranges only
  
  // Layout preferences
  useSideBySideLayout: boolean; // When true, ADC and Wetness graphs shown side-by-side on wide screens
  
  // Data resolution (1-100%)
  dataResolution: number;
  setDataResolution: (resolution: number) => void;
  
  // Auto-refresh settings
  autoRefresh: boolean;
  refreshInterval: number; // milliseconds

  // Shared hover state for synchronized highlighting
  hoveredMeasurementTimestamp: string | null;
  setHoveredMeasurement: (timestamp: string | null) => void;
  
  // Shared hover state for liquid additions
  hoveredLiquidAdditionTimestamp: string | null;
  setHoveredLiquidAddition: (timestamp: string | null) => void;

  // Backend meta (ground truth from Nova backend)
  backendMeta: BackendMeta | null;

  // Annotations
  annotations: Annotation[];
  loadingAnnotations: boolean;
  annotationTagFilter: string | null; // Filter annotations by tag
  loadAnnotations: () => Promise<void>;
  addAnnotation: (data: {
    tags?: string[];
    text: string;
    diaperCounter?: number | null;
    timeRange?: { start: string; end: string } | null;
    measurementTimestamp?: string | null;
  }) => Promise<Annotation>;
  updateAnnotation: (id: string, data: Partial<Annotation>) => Promise<Annotation>;
  deleteAnnotation: (id: string) => Promise<void>;
  setAnnotationTagFilter: (tag: string | null) => void;
  goToAnnotation: (annotation: Annotation) => void;

  // Actions
  loadSources: () => Promise<void>;
  switchSource: (sourceId: string) => Promise<void>;
  loadBuckets: () => Promise<void>;
  switchBucket: (bucket: string | null) => Promise<void>;
  checkServer: () => Promise<void>;
  loadServerConfig: () => Promise<void>;
  toggleProxy: () => Promise<void>;
  loadClipFiles: () => Promise<void>;
  selectClip: (clipAddress: string | null) => void;
  loadClipData: (limit?: number) => Promise<void>;
  /** Load full clip data without limit (use for large clips when user explicitly requests) */
  loadClipDataFull: () => Promise<void>;
  deleteClip: (clipAddress: string) => Promise<void>;
  toggleAutoRefresh: () => void;
  setRefreshInterval: (interval: number) => void;
  loadAvailableCriteria: (siteId: number) => Promise<void>;
  loadCriterion: (criterionId: number, diaperModelCode: number | null) => Promise<void>;
  setCriterionDirect: (criterion: Criterion, diaperModelCode: number | null) => void;
  selectPhase: (phaseId: number, diaperModelCode: number | null) => void;
  clearCriterion: () => void;
  setBrushSelection: (startTime: Date | null, endTime: Date | null) => void;
  setTimelineFilterPreset: (minutes: number | null) => void; // For quick presets (5min, 10min, etc)
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setSelectedDiaperCounter: (counter: number | null) => void;
  setUseDynamicRange: (enabled: boolean) => void;
  setUseSideBySideLayout: (enabled: boolean) => void;
}

export const useClipDataStore = create<ClipDataState>((set, get) => ({
  // Initial state
  serverOnline: false,
  serverConfig: null,
  checkingServer: false,
  firmwareConstants: null,
  availableSources: [],
  activeSourceId: null,
  loadingSources: false,
  availableBuckets: [],
  activeBucket: null,
  loadingBuckets: false,
  error: null,
  clearError: () => set({ error: null }),
  availableClips: [],
  loadingClips: false,
  selectedClipAddress: null,
  clipData: [],
  loadingData: false,
  clipDataLoadLimit: null,
  availableCriteria: [],
  loadingCriteria: false,
  currentCriterion: null,
  currentPhase: null,
  loadingCriterion: false,
  changeNeededPercent: null, // Set via criterion
  leakAdcThresholds: null,
  brushSelection: {
    startTime: null,
    endTime: null,
  }, // null/null = show all data
  selectedDiaperCounter: null, // null = show all, will auto-select newest on load
  useDynamicRange: true, // Default to dynamic range enabled
  useSideBySideLayout: true, // Default to side-by-side layout
  dataResolution: 100, // 100% by default, automatically lowered for large datasets
  autoRefresh: false, // Off by default, user starts when needed
  refreshInterval: 1000, // 1 second default
  hoveredMeasurementTimestamp: null,
  hoveredLiquidAdditionTimestamp: null,
  backendMeta: null,
  annotations: [],
  loadingAnnotations: false,
  annotationTagFilter: null,

  // Load available source folders from server
  loadSources: async () => {
    set({ loadingSources: true });
    try {
      const data = await apiListSources();
      set({ availableSources: data.sources, activeSourceId: data.activeSource, loadingSources: false });
    } catch (error) {
      console.error('Failed to load sources:', error);
      set({ loadingSources: false });
    }
  },

  // Switch to a different source folder and reload everything
  switchSource: async (sourceId: string) => {
    try {
      await apiSelectSource(sourceId);
      set({
        activeSourceId: sourceId,
        activeBucket: null,
        availableBuckets: [],
        selectedClipAddress: null,
        clipData: [],
        annotations: [],
        backendMeta: null,
        brushSelection: { startTime: null, endTime: null },
        clipDataLoadLimit: null,
      });
      await get().loadBuckets();
      await get().loadClipFiles();
    } catch (error: any) {
      console.error('Failed to switch source:', error);
      set({ error: error.message || 'Failed to switch source folder' });
    }
  },

  // Load available buckets from server
  loadBuckets: async () => {
    set({ loadingBuckets: true });
    try {
      const data = await apiListBuckets();
      set({ availableBuckets: data.buckets, activeBucket: data.activeBucket, loadingBuckets: false });
    } catch (error) {
      console.error('Failed to load buckets:', error);
      set({ loadingBuckets: false });
    }
  },

  // Switch to a different bucket and reload clips
  switchBucket: async (bucket: string | null) => {
    try {
      await apiSelectBucket(bucket);
      set({
        activeBucket: bucket,
        selectedClipAddress: null,
        clipData: [],
        annotations: [],
        backendMeta: null,
        brushSelection: { startTime: null, endTime: null },
        clipDataLoadLimit: null,
      });
      await get().loadClipFiles();
    } catch (error) {
      console.error('Failed to switch bucket:', error);
    }
  },

  // Check if server is running
  checkServer: async () => {
    set({ checkingServer: true });
    try {
      const online = await checkServerHealth();
      set({ serverOnline: online, checkingServer: false });
      
      if (online) {
        await get().loadServerConfig();
        getFirmwareConstants().then(fc => set({ firmwareConstants: fc }));
      } else {
        set({ error: 'Clip Monitor server is not reachable. Make sure it is running.' });
      }
    } catch (error) {
      console.error('Failed to check server health:', error);
      set({ serverOnline: false, checkingServer: false, error: 'Failed to connect to Clip Monitor server.' });
    }
  },

  // Load server configuration
  loadServerConfig: async () => {
    try {
      const config = await getServerConfig();
      set({ serverConfig: config });
    } catch (error) {
      console.error('Failed to load server config:', error);
    }
  },

  // Toggle proxy forwarding
  toggleProxy: async () => {
    const { serverConfig } = get();
    if (!serverConfig) return;

    try {
      const newProxyState = !serverConfig.proxyEnabled;
      await updateServerConfig({ proxyEnabled: newProxyState });
      
      set({
        serverConfig: {
          ...serverConfig,
          proxyEnabled: newProxyState,
        },
      });
    } catch (error) {
      console.error('Failed to toggle proxy:', error);
      throw error;
    }
  },

  // Load list of available clip files
  loadClipFiles: async () => {
    set({ loadingClips: true });
    try {
      const clips = await listClipFiles();
      console.log('[Inspector] clipDataStore.loadClipFiles DONE', { clipCount: clips.length, addresses: clips.map(c => c.clipAddress) });
      set({ availableClips: clips, loadingClips: false, error: null });
    } catch (error: any) {
      console.error('Failed to load clip files:', error);
      set({ loadingClips: false, error: `Failed to load clips: ${error.message || 'Unknown error'}` });
      throw error;
    }
  },

  // Select a clip to view
  selectClip: (clipAddress: string | null) => {
    console.log('[Inspector] clipDataStore.selectClip', { clipAddress });
    set({
      selectedClipAddress: clipAddress,
      selectedDiaperCounter: null,
      clipData: [],
      annotations: [],
      backendMeta: null,
      brushSelection: { startTime: null, endTime: null },
      clipDataLoadLimit: null,
    });

    if (clipAddress) {
      get().loadClipData();
      get().loadAnnotations();
      getClipMeta(clipAddress).then(meta => set({ backendMeta: meta }));
    }
  },

  // Load data for selected clip (auto-applies limit for large clips to prevent OOM)
  loadClipData: async (limit?: number) => {
    const { selectedClipAddress, availableClips } = get();
    if (!selectedClipAddress) {
      console.log('[Inspector] clipDataStore.loadClipData: SKIP (no selectedClipAddress)');
      return;
    }

    const DEFAULT_CLIP_LIMIT = 50000;
    const LARGE_CLIP_THRESHOLD = 60000;
    let effectiveLimit = limit;
    if (effectiveLimit === undefined) {
      const clipMeta = availableClips.find((c) => c.clipAddress === selectedClipAddress);
      if (clipMeta && clipMeta.recordCount > LARGE_CLIP_THRESHOLD) {
        effectiveLimit = DEFAULT_CLIP_LIMIT;
      }
    }
    console.log('[Inspector] clipDataStore.loadClipData START', { selectedClipAddress, effectiveLimit, recordCount: availableClips.find((c) => c.clipAddress === selectedClipAddress)?.recordCount });

    set({ loadingData: true });
    try {
      const data = await getClipData(selectedClipAddress, effectiveLimit ?? undefined);
      console.log('[Inspector] clipDataStore.loadClipData DONE', { recordCount: data.length, clipDataLoadLimit: effectiveLimit ?? null });
      set({
        clipData: data,
        loadingData: false,
        clipDataLoadLimit: effectiveLimit ?? null,
      });
    } catch (error: any) {
      console.error('[Inspector] clipDataStore.loadClipData FAILED', error);
      set({ loadingData: false, error: `Failed to load clip data: ${error.message || 'Unknown error'}` });
      throw error;
    }
  },

  // Load full clip data (no limit) - use when user explicitly requests; may be slow/OOM for huge clips
  loadClipDataFull: async () => {
    const { selectedClipAddress } = get();
    if (!selectedClipAddress) {
      console.log('[Inspector] clipDataStore.loadClipDataFull: SKIP (no selectedClipAddress)');
      return;
    }

    console.log('[Inspector] clipDataStore.loadClipDataFull START', { selectedClipAddress });
    set({ loadingData: true });
    try {
      const data = await getClipData(selectedClipAddress);
      console.log('[Inspector] clipDataStore.loadClipDataFull DONE', { recordCount: data.length });
      set({
        clipData: data,
        loadingData: false,
        clipDataLoadLimit: null,
      });
    } catch (error: any) {
      console.error('[Inspector] clipDataStore.loadClipDataFull FAILED', error);
      set({ loadingData: false, error: `Failed to load clip data: ${error.message || 'Unknown error'}` });
      throw error;
    }
  },

  // Load annotations for selected clip
  loadAnnotations: async () => {
    const { selectedClipAddress } = get();
    if (!selectedClipAddress) return;

    set({ loadingAnnotations: true });
    try {
      const annotations = await getAnnotations(selectedClipAddress);
      set({ annotations, loadingAnnotations: false });
    } catch (error) {
      console.error('Failed to load annotations:', error);
      set({ annotations: [], loadingAnnotations: false });
    }
  },

  addAnnotation: async (data) => {
    const { selectedClipAddress } = get();
    if (!selectedClipAddress) throw new Error('No clip selected');

    const annotation = await apiAddAnnotation(selectedClipAddress, data);
    set((state) => ({ annotations: [...state.annotations, annotation] }));
    return annotation;
  },

  updateAnnotation: async (id: string, data: Partial<Annotation>) => {
    const { selectedClipAddress, annotations } = get();
    if (!selectedClipAddress) throw new Error('No clip selected');

    const updated = await apiUpdateAnnotation(selectedClipAddress, id, data);
    set({
      annotations: annotations.map((a) => (a.id === id ? updated : a)),
    });
    return updated;
  },

  deleteAnnotation: async (id: string) => {
    const { selectedClipAddress } = get();
    if (!selectedClipAddress) return;

    await apiDeleteAnnotation(selectedClipAddress, id);
    set((state) => ({ annotations: state.annotations.filter((a) => a.id !== id) }));
  },

  setAnnotationTagFilter: (tag: string | null) => {
    set({ annotationTagFilter: tag });
  },

  goToAnnotation: (annotation: Annotation) => {
    const updates: Partial<ClipDataState> = {};
    if (annotation.diaperCounter !== null) {
      updates.selectedDiaperCounter = annotation.diaperCounter;
    }
    if (annotation.timeRange?.start && annotation.timeRange?.end) {
      updates.brushSelection = {
        startTime: new Date(annotation.timeRange.start),
        endTime: new Date(annotation.timeRange.end),
      };
    } else if (annotation.measurementTimestamp) {
      const ts = new Date(annotation.measurementTimestamp);
      const pad = 2 * 60 * 1000; // 2 min window
      updates.brushSelection = {
        startTime: new Date(ts.getTime() - pad),
        endTime: new Date(ts.getTime() + pad),
      };
    }
    if (Object.keys(updates).length > 0) {
      set(updates);
    }
  },

  // Delete clip data
  deleteClip: async (clipAddress: string) => {
    try {
      await deleteClipData(clipAddress);
      
      // Refresh clip list
      await get().loadClipFiles();
      
      // Clear selection if deleted clip was selected
      if (get().selectedClipAddress === clipAddress) {
        set({ selectedClipAddress: null, clipData: [] });
      }
    } catch (error) {
      console.error('Failed to delete clip data:', error);
      throw error;
    }
  },

  // Toggle auto-refresh
  toggleAutoRefresh: () => {
    set((state) => ({ autoRefresh: !state.autoRefresh }));
  },

  // Set refresh interval
  setRefreshInterval: (interval: number) => {
    set({ refreshInterval: interval });
  },

  // Load all available criteria from backend
  loadAvailableCriteria: async (siteId: number) => {
    set({ loadingCriteria: true });
    try {
      const criteria = await apiLoadAllCriteria(siteId);
      set({ availableCriteria: criteria, loadingCriteria: false });
    } catch (error: any) {
      console.error('Failed to load criteria:', error);
      set({ loadingCriteria: false, availableCriteria: [] });
      set({ error: error?.message || 'Failed to load criteria' });
    }
  },

  // Load criterion from API
  loadCriterion: async (criterionId: number, diaperModelCode: number | null) => {
    set({ loadingCriterion: true });
    try {
      const criterion = await apiLoadCriterion(criterionId);
      set({ currentCriterion: criterion, loadingCriterion: false });

      // Auto-select default phase
      if (criterion.content?.phases?.length > 0) {
        const defaultPhase = criterion.content.phases.find(
          (p: Phase) => p.name && p.name.toLowerCase() === 'default'
        );
        const phase = defaultPhase || criterion.content.phases[0];
        get().selectPhase(phase.id, diaperModelCode);
      }
    } catch (error: any) {
      console.error('Failed to load criterion:', error);
      set({ loadingCriterion: false });
    }
  },

  // Set criterion directly from a pre-built object (e.g. historical config from BackendMeta)
  setCriterionDirect: (criterion: Criterion, diaperModelCode: number | null) => {
    set({ currentCriterion: criterion, loadingCriterion: false });

    if (criterion.content?.phases?.length > 0) {
      const defaultPhase = criterion.content.phases.find(
        (p: Phase) => p.name && p.name.toLowerCase() === 'default'
      );
      const phase = defaultPhase || criterion.content.phases[0];
      get().selectPhase(phase.id, diaperModelCode);
    }
  },

  // Select a phase from current criterion
  selectPhase: (phaseId: number, diaperModelCode: number | null) => {
    const { currentCriterion } = get();
    if (!currentCriterion) return;

    const phase = currentCriterion.content.phases.find((p) => p.id === phaseId);
    if (!phase) return;

    // Find threshold for current diaper model or default
    let threshold = phase.thresholds.find((t) => t.diaperModel === diaperModelCode);
    if (!threshold) {
      threshold = phase.thresholds.find((t) => t.diaperModel == null);
    }

    set({
      currentPhase: phase,
      changeNeededPercent: threshold?.percent || null,
    });
  },

  // Clear criterion selection
  clearCriterion: () => {
    set({
      currentCriterion: null,
      currentPhase: null,
      changeNeededPercent: null,
      leakAdcThresholds: null,
    });
  },

  // Set brush selection (for manual brush drag or programmatic zoom)
  setBrushSelection: (startTime: Date | null, endTime: Date | null) => {
    set({ brushSelection: { startTime, endTime } });
  },

  // Quick preset filters (converts to brush selection based on latest data)
  setTimelineFilterPreset: (minutes: number | null) => {
    const { clipData, selectedDiaperCounter } = get();
    
    // Filter by diaper counter if selected
    const relevantData = selectedDiaperCounter !== null
      ? clipData.filter(d => d.parsed.diaperCounter === selectedDiaperCounter)
      : clipData;
    
    if (minutes === null || relevantData.length === 0) {
      // Show all data
      set({ brushSelection: { startTime: null, endTime: null } });
      return;
    }

    // Calculate time window from latest measurement
    const latestTime = new Date(relevantData[relevantData.length - 1].timestamp);
    const startTime = new Date(latestTime.getTime() - minutes * 60 * 1000);
    
    set({ brushSelection: { startTime, endTime: latestTime } });
  },

  // Zoom in (reduce time window by 50%)
  zoomIn: () => {
    const { brushSelection, clipData, selectedDiaperCounter } = get();
    
    // Filter by diaper counter if selected
    const relevantData = selectedDiaperCounter !== null
      ? clipData.filter(d => d.parsed.diaperCounter === selectedDiaperCounter)
      : clipData;
    
    if (relevantData.length === 0) return;
    
    const allDataStart = new Date(relevantData[0].timestamp);
    const allDataEnd = new Date(relevantData[relevantData.length - 1].timestamp);
    
    const currentStart = brushSelection.startTime || allDataStart;
    const currentEnd = brushSelection.endTime || allDataEnd;
    
    const currentDuration = currentEnd.getTime() - currentStart.getTime();
    const newDuration = currentDuration * 0.5; // Zoom in by 50%
    
    // Center the zoom on the middle of current selection
    const centerTime = currentStart.getTime() + currentDuration / 2;
    const newStart = new Date(centerTime - newDuration / 2);
    const newEnd = new Date(centerTime + newDuration / 2);
    
    set({ brushSelection: { startTime: newStart, endTime: newEnd } });
  },

  // Zoom out (increase time window by 100%)
  zoomOut: () => {
    const { brushSelection, clipData, selectedDiaperCounter } = get();
    
    // Filter by diaper counter if selected
    const relevantData = selectedDiaperCounter !== null
      ? clipData.filter(d => d.parsed.diaperCounter === selectedDiaperCounter)
      : clipData;
    
    if (relevantData.length === 0) return;
    
    const allDataStart = new Date(relevantData[0].timestamp);
    const allDataEnd = new Date(relevantData[relevantData.length - 1].timestamp);
    
    const currentStart = brushSelection.startTime || allDataStart;
    const currentEnd = brushSelection.endTime || allDataEnd;
    
    const currentDuration = currentEnd.getTime() - currentStart.getTime();
    const newDuration = currentDuration * 2; // Zoom out by 100%
    
    // Center the zoom on the middle of current selection
    const centerTime = currentStart.getTime() + currentDuration / 2;
    let newStart = new Date(centerTime - newDuration / 2);
    let newEnd = new Date(centerTime + newDuration / 2);
    
    // Clamp to actual data boundaries
    if (newStart < allDataStart) newStart = allDataStart;
    if (newEnd > allDataEnd) newEnd = allDataEnd;
    
    // If we've reached the full extent, reset to show all
    if (newStart <= allDataStart && newEnd >= allDataEnd) {
      set({ brushSelection: { startTime: null, endTime: null } });
      return;
    }
    
    set({ brushSelection: { startTime: newStart, endTime: newEnd } });
  },

  // Reset zoom (show all data)
  resetZoom: () => {
    set({ brushSelection: { startTime: null, endTime: null } });
  },

  // Set selected diaper counter
  setSelectedDiaperCounter: (counter: number | null) => {
    set({ 
      selectedDiaperCounter: counter,
      // Reset brush selection to show all data for the new diaper
      brushSelection: { startTime: null, endTime: null }
    });
  },

  // Set dynamic range toggle
  setUseDynamicRange: (enabled: boolean) => {
    set({ useDynamicRange: enabled });
  },

  // Set side-by-side layout toggle
  setUseSideBySideLayout: (enabled: boolean) => {
    set({ useSideBySideLayout: enabled });
  },

  // Set data resolution
  setDataResolution: (resolution: number) => {
    set({ dataResolution: Math.max(1, Math.min(100, resolution)) });
  },

  // Shared hover setter (debounced to avoid redundant updates)
  setHoveredMeasurement: (timestamp: string | null) => {
    set((state) => {
      if (state.hoveredMeasurementTimestamp === timestamp) {
        return state;
      }
      return { hoveredMeasurementTimestamp: timestamp } as Partial<ClipDataState>;
    });
  },
  
  // Shared hover setter for liquid additions
  setHoveredLiquidAddition: (timestamp: string | null) => {
    set((state) => {
      if (state.hoveredLiquidAdditionTimestamp === timestamp) {
        return state;
      }
      return { hoveredLiquidAdditionTimestamp: timestamp } as Partial<ClipDataState>;
    });
  },
}));
